import { Controller } from "@hotwired/stimulus"

// Urlaubsformular (base_vacations/_fields): Das Ende folgt dem Beginn, und nach
// jeder Änderung des Zeitraums holt der Controller entprellt Tage, Kontostand
// und Hinweise von POST /vacations/preview (JSON, CSRF-Token aus dem Layout).
// Eine Überschneidung mit einem eigenen Urlaub sperrt das Absenden. Alle Texte
// kommen fertig vom Server und landen als textContent im DOM. Schlägt die
// Anfrage fehl, bleibt das Absenden frei: maßgeblich ist die Validierung beim
// Speichern.
export default class extends Controller {
  static targets = ["start", "end", "panel", "days", "note", "remaining", "hints", "submit", "blockReason"]
  static values = {
    url: String,
    id: String,
    delay: { type: Number, default: 300 },
    // Antwortet der Server nicht, gilt die Vorschau als fehlgeschlagen (Absenden frei)
    timeout: { type: Number, default: 5000 },
  }

  connect() {
    // Nach einem Turbo-Render (z. B. Formular mit 422, per Morphing) bleibt der
    // Controller verbunden, die Vorschau ist aber wieder leer: neu laden.
    this.refresh = () => {
      this.syncMin()
      this.schedule()
    }
    document.addEventListener("turbo:render", this.refresh)
    // Absenden während einer ausstehenden Prüfung: erst prüfen, dann selbst
    // absenden (Review urlaub-ux, Nachprüfung zu Befund 3)
    this.guard = (event) => this.guardSubmit(event)
    this.form = this.element.closest("form")
    this.form?.addEventListener("submit", this.guard)
    this.refresh()
  }

  disconnect() {
    document.removeEventListener("turbo:render", this.refresh)
    this.form?.removeEventListener("submit", this.guard)
    clearTimeout(this.timer)
    this.abort()
  }

  startChanged() {
    const start = this.startTarget.value
    if (start && (!this.endTarget.value || this.endTarget.value < start)) this.endTarget.value = start
    this.syncMin()
    this.schedule()
  }

  // Jede Eingabe macht eine laufende Anfrage ungültig, damit keine Antwort für
  // einen alten Zeitraum mehr ankommt (Review urlaub-ux, Befund 3).
  schedule() {
    clearTimeout(this.timer)
    this.abort()
    this.pending = true
    this.timer = setTimeout(() => this.load(), this.delayValue)
  }

  guardSubmit(event) {
    if (!this.pending) return

    event.preventDefault()
    this.submitWhenChecked = true
    clearTimeout(this.timer)
    if (!this.request) this.load()
  }

  // Nach der Prüfung: nur absenden, wenn nichts sperrt
  release() {
    this.pending = false
    if (!this.submitWhenChecked) return

    this.submitWhenChecked = false
    if (!this.submitTarget.disabled) this.form?.requestSubmit(this.submitTarget)
  }

  syncMin() {
    if (this.startTarget.value) this.endTarget.min = this.startTarget.value
    else this.endTarget.removeAttribute("min")
  }

  async load() {
    this.abort()
    const start = this.startTarget.value
    const end = this.endTarget.value
    if (!start || !end) {
      this.clear()
      return this.release()
    }

    const controller = new AbortController()
    this.request = controller
    const timeout = setTimeout(() => controller.abort(), this.timeoutValue)
    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        credentials: "same-origin",
        signal: controller.signal,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": this.csrfToken,
        },
        body: JSON.stringify({ vacation: { start_date: start, end_date: end }, id: this.idValue }),
      })
      if (!response.ok) throw new Error(`Vorschau: ${response.status}`)
      const data = await response.json()
      if (this.request === controller && this.matches(start, end)) {
        this.request = null
        this.render(data)
        this.release()
      }
    } catch {
      // Netzfehler, Fehlerstatus oder Zeitüberschreitung (Befund 4). Wurde die
      // Anfrage durch eine neue Eingabe ersetzt, ist this.request schon leer.
      if (this.request === controller) {
        this.request = null
        this.clear()
        this.release()
      }
    } finally {
      clearTimeout(timeout)
      if (this.request === controller) this.request = null
    }
  }

  matches(start, end) {
    return this.startTarget.value === start && this.endTarget.value === end
  }

  abort() {
    this.request?.abort()
    this.request = null
  }

  render(data) {
    this.panelTarget.hidden = !data.valid
    this.daysTarget.textContent = data.summary || ""
    this.noteTarget.textContent = data.summary_note || ""
    this.remainingTarget.textContent = (data.remaining || []).join(" · ")

    const items = (data.hints || []).map((hint) => {
      const item = document.createElement("li")
      item.textContent = hint.text
      item.dataset.kind = hint.kind
      if (hint.blocking) item.dataset.blocking = ""
      return item
    })
    this.hintsTarget.replaceChildren(...items)
    this.hintsTarget.hidden = items.length === 0
    this.block(data.blocking ? data.blocked_reason : null)
  }

  clear() {
    this.panelTarget.hidden = true
    this.hintsTarget.replaceChildren()
    this.hintsTarget.hidden = true
    this.block(null)
  }

  block(reason) {
    const blocked = Boolean(reason)
    this.submitTarget.disabled = blocked
    this.blockReasonTarget.hidden = !blocked
    this.blockReasonTarget.textContent = reason || ""
    if (blocked) this.submitTarget.setAttribute("aria-describedby", this.blockReasonTarget.id)
    else this.submitTarget.removeAttribute("aria-describedby")
  }

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }
}
